import { combineLatest, map, Observable } from 'rxjs';
import type { LiquidityAmount, Token } from '@akropolis-web/primitives';

import { protocolsApi, ratingsApi } from 'api/modules';
import {
  FallbackedTotalUSD,
  Rating,
  ProtocolAvgRatingInfo,
  ProductSlug,
  VersionSlug,
  NetworkSlug,
} from 'domain/types';
import { getMarketTokens, getWorstRating } from 'domain/utils';
import { Either } from 'utils/either';
import { calcFallbackedTotalUsd } from 'api/utils/calcFallbackedTotalUSD';

import { getProtocolAvgRatingInfo } from '../ratings';

export type ProtocolRatingsPageData = {
  marketsData: MarketsTableData[];
  ratingInfo: Either<ProtocolAvgRatingInfo>;
};

export type MarketsTableData = {
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  network: NetworkSlug;
  marketId: string;
  marketName: string;
  rating: Either<Rating>;
  tokens: Token[];
  totalDeposit: FallbackedTotalUSD;
  partialTotalDeposit: LiquidityAmount;
};

export function getProtocolRatingsPageData({
  productSlug,
  versionSlug,
  network,
}: {
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  network: NetworkSlug;
}): Observable<ProtocolRatingsPageData> {
  return combineLatest([
    protocolsApi.getLendingInfo$(true, { productSlug, versionSlug, network }),
    protocolsApi.getMarketsMetrics$('latest', { productSlug, versionSlug, network }),
    ratingsApi.getMarketsRatings$({ productSlug, versionSlug, network }),
    getProtocolAvgRatingInfo({ network, productSlug, versionSlug }),
  ]).pipe(
    map(
      ([protocolInfo, allMarketsMetrics, marketsRatings, ratingInfo]): ProtocolRatingsPageData => {
        return {
          ratingInfo,
          marketsData: allMarketsMetrics.map(marketMetrics => {
            const market = protocolInfo.markets.find(x => x.vid === marketMetrics.marketId);
            const tokens = getMarketTokens({
              protocolInfo,
              marketId: marketMetrics.marketId,
            });

            const marketRating = getWorstRating(marketsRatings.get(marketMetrics.marketId) || []);

            const data: MarketsTableData = {
              productSlug,
              versionSlug,
              network,
              marketId: marketMetrics.marketId,
              marketName: market?.marketName || 'Unknown market',
              tokens,
              rating: marketRating.map(r => r.rating),
              totalDeposit: calcFallbackedTotalUsd(marketMetrics.assetTotals, 'totalDeposit', true),
              partialTotalDeposit: marketMetrics.partialBalanceUSD.totalDeposit,
            };
            return data;
          }),
        };
      },
    ),
  );
}
